<template>
  <div class="vap-page">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-page-header">
      <h1>Travel Discrepancy Report</h1>
      <div class="btns-container">
        <VasionButton
          id="share"
          :classProp="'secondary'"
          :isDisabled="disableRunButtons || disableShareButton"
          :ripple="true"
          @vasionButtonClicked="generatePDF"
        >
          Share
        </VasionButton>
        <VasionButton
          id="run-report"
          type="button"
          :classProp="'primary'"
          :isDisabled="disableRunButtons"
          :ripple="true"
          @vasionButtonClicked="runDiscrepancyReport"
        >
          Run Report
        </VasionButton>
      </div>
    </div>
    <div class="vap-page-content">
      <form @submit.prevent="runDiscrepancyReport($event)">
        <div class="setup-row travel-file-row">
          <VasionInput
            id="travel-file"
            v-model="newTravelFile"
            class="row-field browse-input"
            title="TRAVEL FILE"
            inputType="top-white"
            name="travel-file"
            placeholder="Select File..."
            :required="false"
            :width="'396'"
          />
          <VasionButton
            id="travel-file-folder-button"
            class="browse-button"
            :classProp="'primary'"
            @vasionButtonClicked="toggleBrowseForTravelFileDialog()"
          >
            Browse
          </VasionButton>
          <VasionFileUploadDialog
            :serverOptions="serverOptions"
            :show.sync="showUploadDialog"
            :acceptedFileTypes="['.xlsx', '.xls']"
            :filePondLabel="filePondLabel"
            @allFilesUploaded="allFilesUploaded"
          />
        </div>
        <div class="setup-row">
          <VasionDropList
            v-slot="slotItem"
            v-model="databaseConnection"
            class="row-field"
            :dataArray="databaseConnectionList"
            width="396"
            title="DATABASE CONNECTION"
            placeholder="Select Connection..."
            type="plain-list"
            displayName="name"
            valueName="value"
            @input="isDirty = true"
          >
            {{ slotItem.item.name }}
          </VasionDropList>
          <VasionButton
            name="add-database-connection"
            :icon="'VasionAddIcon'"
            title="Add Database Connection"
            class="add-database-connection-icon-button"
            @vasionButtonClicked="addConnectionClick()"
          />
          <VasionButton
            v-if="shouldDeleteConnectionShow"
            name="delete-database-connection"
            :icon="'VasionDeleteIcon'"
            title="Delete Database Connection"
            class="add-database-connection-icon-button"
            @vasionButtonClicked="deleteConnectionClick()"
          />
        </div>
        <div class="setup-row query-row">
          <span class="vasion-input-label-top">QUERY</span>
          <div class="query-content">
            <textarea
              id="query-textarea"
              ref="query"
              v-model="queryText"
              rows="12"
              class="vasion-textarea"
              name="query-textarea"
            />
          </div>
        </div>
        <div class="setup-row date-row">
          <VasionDateTimeField
            v-model="minDate"
            label="START DATE"
            type="date"
            :required="false"
            width="190px"
          />
          <VasionDateTimeField
            v-model="maxDate"
            label="END DATE"
            type="date"
            :required="false"
            width="190px"
          />
        </div>
        <div class="setup-row query-row">
          <span class="vasion-input-label-top">EMPLOYEE LOOKUP SQL</span>
          <div class="query-content">
            <textarea
              id="employee-lookup-textarea"
              ref="employeeLookup"
              v-model="employeeLookupSql"
              rows="6"
              class="vasion-textarea"
              name="employee-lookup-textarea"
            />
          </div>
        </div>
        <VasionSnackbar
          id="import-snack"
          :showSnackbarBool.sync="showSnackbarBool"
          :snackbarImage="snackbarImage"
          :snackbarSubTitle="snackbarSubTitle"
          :snackbarTitle="snackbarTitle"
        />
      </form>
    </div>

    <md-dialog
      id="add-connection-dialog"
      class="add-connection-dialog-window"
      :md-active.sync="showAddConnectionDialog"
      :md-click-outside-to-close="false"
    >
      <EditDatabaseConnection
        @cancelConnectionClick="toggleAddConnectionDialog"
        @saveConnectionClick="connectionSaved"
      />
    </md-dialog>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
// Import stylesheet
import 'vue-loading-overlay/dist/vue-loading.css';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import { toBase64 } from '@/store/helperModules/storage.module'
import EditDatabaseConnection from '@/components/fieldMapping/EditDatabaseConnection.vue'

export default {
  name: 'TheDiscrepancyReport',
  components: {
    Loading,
    EditDatabaseConnection,
  },
  data: function () {
    return {
      databaseConnection: '',
      databaseConnectionId: '',
      discrepancyReportGUID: '',
      employeeLookupSql: '',
      filePondLabel: `<span class="filepond-drag">Drag Excel File Here</span>
                <br/><br/>
                <span class="filepond-label-action">or click to upload your Excel File</span>`,
      isDirty: false,
      isLoading: false,
      loaderBackgroundColor,
      loaderColor,
      maxDate: '',
      minDate: '',
      newTravelFile: '',
      queryText: '',
      serverOptions: { process: this.processHandler },
      showAddConnectionDialog: false,
      showSnackbarBool: false,
      showUploadDialog: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      uploadedTravelFileString: '',
    }
  },
  computed: {
    databaseConnectionList() {
      return this.$store.state.fieldMapping.databaseConnectionList.map((f) => {
        return {
          name: f.name,
          value: f.id,
        }
      })
    },
    disableRunButtons() {
      const hasConnection = this.databaseConnection && typeof this.databaseConnection === 'object' && this.databaseConnection.value
      return !hasConnection || this.uploadedTravelFileString === '' || !this.queryText || this.queryText.trim() === ''
    },
    disableShareButton() { return this.discrepancyReportGUID === undefined || this.discrepancyReportGUID === '' },
  },
  async created() {
    this.isLoading = true
    await this.$store.dispatch('fieldMapping/getDatabaseConnections')

    const [savedConnectionId, savedQuery, savedEmployeeLookupSql, savedMinDate, savedMaxDate] = await Promise.all([
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportConnectionId'),
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportQuery'),
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportEmployeeLookupSql'),
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportMinDate'),
      this.$store.dispatch('common/getConfig', 'DiscrepancyReportMaxDate'),
    ])

    if (savedConnectionId) {
      const connId = parseInt(savedConnectionId, 10)
      if (Number.isFinite(connId)) {
        const matchingConnection = this.databaseConnectionList.find((c) => c.value === connId)
        if (matchingConnection) {
          this.databaseConnection = matchingConnection
          this.databaseConnectionId = connId
        }
      }
    }
    if (savedQuery) {
      this.queryText = savedQuery
    }
    if (savedEmployeeLookupSql) {
      this.employeeLookupSql = savedEmployeeLookupSql
    }
    if (savedMinDate) {
      this.minDate = savedMinDate.split('T')[0]
    }
    if (savedMaxDate) {
      this.maxDate = savedMaxDate.split('T')[0]
    }

    this.isLoading = false
  },
  methods: {
    allFilesUploaded() {
      this.showUploadDialog = false
    },
    async generatePDF() {
      this.isLoading = true
      this.snackbarImage = false
      this.snackbarTitle = 'ERROR'
      try {
        const downloadResult = await this.$store.dispatch('reporting/downloadDiscrepancyReport', { GUID: this.discrepancyReportGUID, DownloadExcel: true })
        if (downloadResult) {
          // eslint-disable-next-line
          const fileDownload = require('js-file-download')
          fileDownload(downloadResult, 'TravelDiscrepancyReport.xlsx')
          await this.$amplitude.trackEvent('Report Shared', { reportType: 'Discrepancy' })
        } else {
          this.snackbarSubTitle = 'An error occurred while generating the report. Please try again.'
          this.showSnackbarBool = true
        }
      } catch (error) {
        this.snackbarSubTitle = this.getErrorMessage(error, 'An error occurred while generating the report. Please try again.')
        this.showSnackbarBool = true
      } finally {
        this.isLoading = false
      }
    },
    addConnectionClick() {
      this.toggleAddConnectionDialog(true)
    },
    async connectionSaved() {
      await this.$store.dispatch('fieldMapping/getDatabaseConnections')
      this.toggleAddConnectionDialog(false)
    },
    async deleteConnectionClick() {
      if (!this.databaseConnection || !this.databaseConnection.value || this.databaseConnection.value <= 0) {
        this.snackbarTitle = 'ERROR'
        this.snackbarSubTitle = 'Please select a database connection to delete.'
        this.snackbarImage = false
        this.showSnackbarBool = true
        return
      }
      const deleted = await this.$store.dispatch('fieldMapping/deleteDatabaseConnection', this.databaseConnection.value)
      if (deleted) {
        this.snackbarTitle = 'SUCCESS'
        this.snackbarSubTitle = `Successfully deleted "${this.databaseConnection.name}".`
        this.snackbarImage = true
        await this.$store.dispatch('fieldMapping/getDatabaseConnections')
        if (this.databaseConnection.value === this.databaseConnectionId) {
          this.databaseConnectionId = 0
        }
        this.databaseConnection = this.databaseConnectionList.find((c) => c.value === this.databaseConnectionId) || ''
      } else {
        this.snackbarTitle = 'ERROR'
        this.snackbarSubTitle = `Unable to delete "${this.databaseConnection.name}".`
        this.snackbarImage = false
      }
      this.showSnackbarBool = true
    },
    processHandler: async function (fieldName, file, metadata, load, error, progress, abort) {
      const isExcel = file?.name && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))
      if (isExcel) {
        this.uploadedTravelFileString = await toBase64(file)
        const base64String = this.uploadedTravelFileString.split('base64,')[1]
        if (!base64String) {
          error(true)
          this.snackbarTitle = 'ERROR'
          this.snackbarSubTitle = 'Empty Excel file can not be used'
          this.showSnackbarBool = true
          return
        }
        progress(true, 0, 1024)
        load(file?.name)
        this.newTravelFile = file?.name
      } else {
        error(true)
        this.showSnackbarBool = true
        this.snackbarImage = false
        this.snackbarTitle = 'ERROR'
        this.snackbarSubTitle = 'Can only upload Excel files (.xlsx or .xls).'
        return
      }
      return {
        abort: () => {
          abort()
        },
      }
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      const data = error.response?.data
      const serverMessage = data?.Value ?? data?.message ?? data?.Message ?? (typeof data === 'string' ? data : null)
      if (serverMessage) return serverMessage
      return fallback
    },
    async runDiscrepancyReport(event) {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault()
      }
      this.isLoading = true
      this.snackbarImage = false
      this.snackbarTitle = 'ERROR'

      try {
        const payload = {
          DbConnectionID: this.databaseConnection?.value,
          QueryText: this.queryText,
          TravelFileUpload: this.uploadedTravelFileString,
          MinDate: this.minDate || null,
          MaxDate: this.maxDate || null,
          EmployeeLookupSql: this.employeeLookupSql || null,
        }

        const resp = await this.$store.dispatch('reporting/runDiscrepancyReport', payload)
        if (!resp || resp.GUID == null) {
          this.snackbarSubTitle = 'An error occurred while running the report. Please try again.'
          this.showSnackbarBool = true
          this.isLoading = false
          return
        }
        this.discrepancyReportGUID = resp.GUID

        await this.$amplitude.trackEvent('Report Run', { reportType: 'Discrepancy' })

        const excelResult = await this.$store.dispatch('reporting/downloadDiscrepancyReport', { GUID: this.discrepancyReportGUID, DownloadExcel: true })
        if (excelResult) {
          // eslint-disable-next-line
          const fileDownload = require('js-file-download')
          fileDownload(excelResult, 'TravelDiscrepancyReport.xlsx')
          this.snackbarSubTitle = 'Report ran successfully. Your download should begin shortly.'
          this.snackbarImage = true
          this.snackbarTitle = 'SUCCESS'
          this.showSnackbarBool = true
        } else {
          this.snackbarSubTitle = 'An error occurred while downloading the report. Please try again.'
          this.showSnackbarBool = true
        }
      } catch (error) {
        this.snackbarSubTitle = this.getErrorMessage(error, 'An error occurred while running the report. Please try again.')
        this.showSnackbarBool = true
        return
      } finally {
        this.isLoading = false
      }
    },
    toggleAddConnectionDialog(value) {
      if (value !== undefined && value !== null && typeof value === 'boolean') {
        this.showAddConnectionDialog = value
      } else {
        this.showAddConnectionDialog = !this.showAddConnectionDialog
      }
    },
    toggleBrowseForTravelFileDialog() { this.showUploadDialog = !this.showUploadDialog },
  },
}
</script>
<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';

  .setup-row {
    display: flex;
    align-items: flex-start;
    width: 100%;
    margin: 6px 0;

    &.travel-file-row {
      align-items: flex-end;
    }

    &.date-row {
      gap: 20px;
    }

    > .vasion-droplist,
    > .vasion-input {
      width: 396px;
      margin-right: 14px;
    }

    > .vasion-button {
      margin-top: 18px;

      &.browse-button {
        margin-left: -10px;
        margin-right: 3px;
        margin-top: 0;
      }

      &.add-database-connection-icon-button {
        margin-left: -10px;
        margin-right: 3px;
        margin-top: 20px;
      }
    }

    &.query-row {
      display: block;
      margin-top: 13px;

      .vasion-input-label-top {
        display: block;
        margin-bottom: 4px;
      }

      .query-content textarea.vasion-textarea {
        width: 100%;
        max-width: 808px;
        height: 300px;
      }
    }
  }

  .vasion-run-report-prompt {
    margin-top: 200px;
  }
</style>
